import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useApiMutation } from '@/hooks/useApiMutation'
import { useAdminFilters } from '@/hooks/useAdminFilters'
import { formatDate, formatNumber } from '@/ui/format'
import { Alert, Badge, Button, DatePicker, Input, Modal, Pagination, Select, StatCard, Table, ViewReasonModal } from '@/ui'
import type { TableColumn } from '@/ui'
import styles from './AIReferencePhotos.module.css'

// --- 타입 ---

interface RefPhotoUser {
  email: string
  name: string | null
}

// 리스트 응답: 사진 URL 미포함 (개인정보 최소화). 메타만.
interface RefPhoto {
  id: string
  userId: string | null
  role: string
  faceDetected: boolean
  qualityVerdict: string | null       // 'warn' | 'ok' | null (구 데이터)
  qualityWarnings: string[]           // warn 사유 (예: ['pitch','brightness']), 없으면 []
  isActive: boolean
  createdAt: string
  user: RefPhotoUser
}

interface UserGroup {
  userId: string | null
  user: RefPhotoUser
  photos: RefPhoto[]
}

interface RefPhotosResponse {
  photos: RefPhoto[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
  stats: {
    totalCount: number
    activeCount: number
    groomCount: number
    brideCount: number
    faceDetectedCount: number
    uniqueUsers: number
    warnCount: number
  }
}

// 열람 응답: 사유 입력 후에만 URL 반환
interface ViewPhoto {
  id: string
  role: string
  originalUrl: string
  faceDetected: boolean
  isActive: boolean
  createdAt: string
}

// --- 상수 ---

const ROLE_OPTIONS = [
  { value: '', label: '전체 역할' },
  { value: 'GROOM', label: '신랑' },
  { value: 'BRIDE', label: '신부' },
]

const ACTIVE_OPTIONS = [
  { value: 'true', label: '활성만' },
  { value: '', label: '전체' },
  { value: 'false', label: '비활성만' },
]

const FACE_OPTIONS = [
  { value: '', label: '얼굴 감지 전체' },
  { value: 'true', label: '얼굴 감지 O' },
  { value: 'false', label: '얼굴 감지 X' },
]

// --- 컴포넌트 ---

export function AIReferencePhotos() {
  const { filters, setFilters, resetFilters } = useAdminFilters({ pageSize: 60 })

  const [roleFilter, setRoleFilter] = useState('')
  const [activeFilter, setActiveFilter] = useState('true')
  const [faceFilter, setFaceFilter] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [searchDebounced, setSearchDebounced] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  // 열람 사유 입력 대상 (유저)
  const [reasonTarget, setReasonTarget] = useState<{ userId: string; label: string } | null>(null)
  const [lightbox, setLightbox] = useState<{ urls: string[]; index: number; label: string } | null>(null)

  const viewMutation = useApiMutation<{ photos: ViewPhoto[] }, { userId: string; reason: string }>(
    '/api/admin/ai-reference-photos/view',
  )

  useEffect(() => {
    resetFilters()
  }, [resetFilters])

  // 300ms debounce
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(searchInput)
      setFilters({ page: 1 })
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput, setFilters])

  const params = useMemo(() => {
    const p = new URLSearchParams()
    p.set('page', String(filters.page))
    p.set('pageSize', String(filters.pageSize))
    p.set('sortBy', filters.sortBy)
    p.set('sortOrder', filters.sortOrder)
    if (roleFilter) p.set('role', roleFilter)
    if (activeFilter) p.set('isActive', activeFilter)
    if (faceFilter) p.set('faceDetected', faceFilter)
    if (searchDebounced) p.set('search', searchDebounced)
    if (startDate) p.set('startDate', new Date(startDate).toISOString())
    if (endDate) p.set('endDate', new Date(endDate + 'T23:59:59').toISOString())
    return p.toString()
  }, [filters, roleFilter, activeFilter, faceFilter, searchDebounced, startDate, endDate])

  const { data, isLoading, error } = useApiFetch<RefPhotosResponse>(
    `/api/admin/ai-reference-photos?${params}`,
  )

  const handleResetFilters = useCallback(() => {
    setRoleFilter('')
    setActiveFilter('true')
    setFaceFilter('')
    setSearchInput('')
    setSearchDebounced('')
    setStartDate('')
    setEndDate('')
    resetFilters()
  }, [resetFilters])

  // 사용자별 그룹핑 (현재 페이지 내)
  const groups: UserGroup[] = useMemo(() => {
    const photos = data?.photos ?? []
    const map = new Map<string, UserGroup>()
    for (const p of photos) {
      const key = p.userId ?? '__anon__'
      const existing = map.get(key)
      if (existing) {
        existing.photos.push(p)
      } else {
        map.set(key, { userId: p.userId, user: p.user, photos: [p] })
      }
    }
    return Array.from(map.values())
  }, [data])

  // 라이트박스 키보드 네비
  useEffect(() => {
    if (!lightbox) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight') setLightbox(prev => prev ? { ...prev, index: (prev.index + 1) % prev.urls.length } : prev)
      else if (e.key === 'ArrowLeft') setLightbox(prev => prev ? { ...prev, index: (prev.index - 1 + prev.urls.length) % prev.urls.length } : prev)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightbox])

  const openReason = (g: UserGroup) => {
    if (!g.userId) return
    viewMutation.reset()
    setReasonTarget({ userId: g.userId, label: g.user.name ?? g.user.email ?? '익명' })
  }

  const handleConfirmView = async (reason: string) => {
    if (!reasonTarget) return
    const res = await viewMutation.trigger({ userId: reasonTarget.userId, reason })
    if (res?.photos) {
      setLightbox({ urls: res.photos.map(p => p.originalUrl), index: 0, label: reasonTarget.label })
      setReasonTarget(null)
      viewMutation.reset()
    }
  }

  // 그룹 단위 집계 (역할/얼굴/품질경고/최근일시)
  const groupMeta = (g: UserGroup) => {
    let groom = 0, bride = 0, face = 0, warn = 0, latest = ''
    for (const p of g.photos) {
      if (p.role === 'GROOM') groom++
      else if (p.role === 'BRIDE') bride++
      if (p.faceDetected) face++
      if (p.qualityVerdict === 'warn') warn++
      if (p.createdAt > latest) latest = p.createdAt
    }
    return { groom, bride, face, warn, latest }
  }

  const columns: TableColumn<UserGroup>[] = [
    {
      key: 'user',
      label: '유저',
      render: (g) => (
        <div className={styles.userCell}>
          <span className={styles.userName}>{g.user.name ?? g.user.email ?? '익명'}</span>
          {g.user.email && g.user.name && <span className={styles.userEmailDim}>{g.user.email}</span>}
        </div>
      ),
    },
    {
      key: 'role',
      label: '역할',
      width: '140px',
      render: (g) => {
        const { groom, bride } = groupMeta(g)
        return (
          <div className={styles.roleCell}>
            {groom > 0 && <Badge color="blue">신랑 {groom}</Badge>}
            {bride > 0 && <Badge color="green">신부 {bride}</Badge>}
          </div>
        )
      },
    },
    { key: 'count', label: '장수', width: '70px', align: 'right', render: (g) => `${g.photos.length}장` },
    {
      key: 'face',
      label: '얼굴 감지',
      width: '90px',
      align: 'right',
      render: (g) => {
        const { face } = groupMeta(g)
        return `${face}/${g.photos.length}`
      },
    },
    {
      key: 'quality',
      label: '품질 경고',
      width: '160px',
      render: (g) => {
        const { warn } = groupMeta(g)
        if (warn === 0) return <span className={styles.userEmailDim}>—</span>
        // 그룹 내 warn 사진들의 사유를 합쳐 상위 몇 개만 노출
        const reasons = Array.from(
          new Set(g.photos.flatMap((p) => (p.qualityVerdict === 'warn' ? p.qualityWarnings : []))),
        )
        return (
          <div className={styles.roleCell}>
            <Badge color="yellow">경고 {warn}</Badge>
            {reasons.length > 0 && (
              <span className={styles.userEmailDim}>{reasons.join(', ')}</span>
            )}
          </div>
        )
      },
    },
    {
      key: 'latest',
      label: '최근 업로드',
      width: '150px',
      render: (g) => formatDate(groupMeta(g).latest, 'yyyy.M.d HH:mm'),
    },
    {
      key: 'action',
      label: '',
      width: '80px',
      align: 'right',
      render: (g) => (
        <Button size="sm" variant="outline" onClick={() => openReason(g)} disabled={!g.userId}>
          보기
        </Button>
      ),
    },
  ]

  return (
    <div className={styles.root}>
      {/* 통계 카드 */}
      <div className={styles.statGrid}>
        <StatCard label="총 참조사진" value={formatNumber(data?.stats.totalCount ?? 0)} />
        <StatCard label="활성" value={formatNumber(data?.stats.activeCount ?? 0)} />
        <StatCard label="신랑 / 신부" value={`${formatNumber(data?.stats.groomCount ?? 0)} / ${formatNumber(data?.stats.brideCount ?? 0)}`} />
        <StatCard label="얼굴 감지" value={formatNumber(data?.stats.faceDetectedCount ?? 0)} />
        <StatCard label="품질 경고" value={formatNumber(data?.stats.warnCount ?? 0)} />
        <StatCard label="고유 유저 수" value={formatNumber(data?.stats.uniqueUsers ?? 0)} />
      </div>

      {/* 필터 */}
      <div className={styles.filterBar}>
        <Input
          size="sm"
          placeholder="유저 이메일/이름 검색"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className={styles.search}
        />
        <Select size="sm" value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setFilters({ page: 1 }) }} options={ROLE_OPTIONS} />
        <Select size="sm" value={activeFilter} onChange={(e) => { setActiveFilter(e.target.value); setFilters({ page: 1 }) }} options={ACTIVE_OPTIONS} />
        <Select size="sm" value={faceFilter} onChange={(e) => { setFaceFilter(e.target.value); setFilters({ page: 1 }) }} options={FACE_OPTIONS} />
        <DatePicker
          size="sm"
          value={startDate}
          onChange={(_d: Date | null, formatted: string) => { setStartDate(formatted); setFilters({ page: 1 }) }}
          placeholder="시작일"
        />
        <DatePicker
          size="sm"
          value={endDate}
          onChange={(_d: Date | null, formatted: string) => { setEndDate(formatted); setFilters({ page: 1 }) }}
          placeholder="종료일"
        />
        <Button size="sm" variant="ghost" onClick={handleResetFilters}>초기화</Button>
      </div>

      {/* 에러 */}
      {error && <Alert variant="danger">{error}</Alert>}

      {/* 유저 리스트 (사진은 [보기]+사유 입력 후에만 표시) */}
      <Table
        columns={columns}
        data={groups}
        rowKey={(g) => g.userId ?? '__anon__'}
        isLoading={isLoading && !data}
        emptyMessage="조건에 맞는 참조사진이 없습니다."
      />

      {/* 페이지네이션 */}
      {data && data.pagination.totalPages > 1 && (
        <div className={styles.paginationWrap}>
          <Pagination
            current={data.pagination.page}
            total={data.pagination.totalPages}
            onChange={(page) => setFilters({ page })}
          />
        </div>
      )}

      {/* 열람 사유 입력 */}
      <ViewReasonModal
        open={!!reasonTarget}
        onClose={() => { setReasonTarget(null); viewMutation.reset() }}
        onConfirm={handleConfirmView}
        title={reasonTarget ? `${reasonTarget.label} 참조사진 열람` : '열람 사유 입력'}
        description="얼굴 사진(생체정보) 열람은 접속기록으로 남습니다. 열람 사유를 입력해 주세요."
        loading={viewMutation.isLoading}
        error={viewMutation.error}
      />

      {/* 라이트박스 */}
      <Modal
        open={!!lightbox}
        onClose={() => setLightbox(null)}
        size="xl"
        title={lightbox ? `${lightbox.label} (${lightbox.index + 1}/${lightbox.urls.length})` : undefined}
      >
        {lightbox && (
          <div className={styles.lightbox}>
            <button
              type="button"
              className={styles.lightboxNav}
              onClick={() => setLightbox({ ...lightbox, index: (lightbox.index - 1 + lightbox.urls.length) % lightbox.urls.length })}
              aria-label="이전"
              disabled={lightbox.urls.length < 2}
            >
              <ChevronLeft size={24} />
            </button>
            <img src={lightbox.urls[lightbox.index]} alt={`${lightbox.label} ${lightbox.index + 1}`} className={styles.lightboxImg} />
            <button
              type="button"
              className={styles.lightboxNav}
              onClick={() => setLightbox({ ...lightbox, index: (lightbox.index + 1) % lightbox.urls.length })}
              aria-label="다음"
              disabled={lightbox.urls.length < 2}
            >
              <ChevronRight size={24} />
            </button>
          </div>
        )}
        {lightbox && (
          <div className={styles.lightboxFooter}>
            <a href={lightbox.urls[lightbox.index]} target="_blank" rel="noopener noreferrer" className={styles.lightboxLink}>
              원본 열기
            </a>
          </div>
        )}
      </Modal>
    </div>
  )
}
